import {
  getEcho,
  isRealtimeEnabled,
  type MessagingMessageCreatedPayload
} from '@/shared/realtime/echo'

const CHANNEL_PREFIX = 'messaging.phone-number.'
const CREATED_EVENT = '.MessagingMessageCreated'
const UPDATED_EVENT = '.MessagingMessageUpdated'

export type MessagingRealtimeHandler = (payload: MessagingMessageCreatedPayload) => void

type ChannelEntry = {
  refCount: number
  createdHandlers: Set<MessagingRealtimeHandler>
  updatedHandlers: Set<MessagingRealtimeHandler>
}

const channels = new Map<string, ChannelEntry>()

function channelNameFor(phoneNumberId: string): string {
  return `${CHANNEL_PREFIX}${phoneNumberId}`
}

/**
 * Refcounted Echo subscriptions for messaging DIDs.
 * Shared by Messages tab UI and app-wide notification listener so one leave
 * does not tear down the other.
 */
export function subscribeMessagingPhoneNumber(
  phoneNumberId: string,
  handlers: {
    onCreated?: MessagingRealtimeHandler
    onUpdated?: MessagingRealtimeHandler
  }
): () => void {
  const id = String(phoneNumberId || '')
  if (!id || !isRealtimeEnabled()) {
    return () => undefined
  }

  const echo = getEcho()
  if (!echo) {
    return () => undefined
  }

  const name = channelNameFor(id)
  let entry = channels.get(name)

  if (!entry) {
    entry = {
      refCount: 0,
      createdHandlers: new Set(),
      updatedHandlers: new Set()
    }
    channels.set(name, entry)

    echo
      .private(name)
      .listen(CREATED_EVENT, (payload: MessagingMessageCreatedPayload) => {
        const current = channels.get(name)
        if (!current) return
        for (const handler of current.createdHandlers) {
          handler(payload)
        }
      })
      .listen(UPDATED_EVENT, (payload: MessagingMessageCreatedPayload) => {
        const current = channels.get(name)
        if (!current) return
        for (const handler of current.updatedHandlers) {
          handler(payload)
        }
      })
  }

  entry.refCount += 1
  if (handlers.onCreated) entry.createdHandlers.add(handlers.onCreated)
  if (handlers.onUpdated) entry.updatedHandlers.add(handlers.onUpdated)

  let active = true
  return () => {
    if (!active) return
    active = false
    const current = channels.get(name)
    if (!current) return

    if (handlers.onCreated) current.createdHandlers.delete(handlers.onCreated)
    if (handlers.onUpdated) current.updatedHandlers.delete(handlers.onUpdated)
    current.refCount = Math.max(0, current.refCount - 1)

    if (current.refCount === 0) {
      channels.delete(name)
      try {
        getEcho()?.leave(name)
      } catch (error) {
        console.warn('[MessagingChannelBus] leave failed:', error)
      }
    }
  }
}

/** Drop every messaging channel (logout / full Echo reset). */
export function clearMessagingChannelBus(): void {
  const echo = getEcho()
  for (const name of channels.keys()) {
    try {
      echo?.leave(name)
    } catch {
      // ignore
    }
  }
  channels.clear()
}
